// ./src/components/features/Connectors/ConfigEditors/AzureQueueConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  NumberInput,
  Text,
  Anchor,
  Alert,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AzureQueueConnectorConfig } from "@/types";

export interface AzureQueueConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AzureQueueConnectorConfig = {
  connectionString: "",
  queueName: "",
  messageTtlSeconds: 604800, // 7 days (Azure default)
};

/**
 * Parse and validate Azure Queue connector configuration
 */
const parseAzureQueueConfig = (
  jsonString: string
): AzureQueueConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);

  // Ensure messageTtlSeconds is a valid number
  if (
    typeof parsed.messageTtlSeconds !== "number" ||
    isNaN(parsed.messageTtlSeconds)
  ) {
    parsed.messageTtlSeconds = DEFAULT_CONFIG.messageTtlSeconds;
  }

  return parsed;
};

/**
 * Format TTL value for display
 */
const formatTtlDescription = (seconds: number | undefined): string => {
  if (seconds === undefined || seconds === null) {
    return "7 days (default)";
  }
  if (seconds === -1) {
    return "Messages never expire";
  }
  if (seconds < 60) {
    return `${seconds} second${seconds !== 1 ? "s" : ""}`;
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600);
    return `${hours} hour${hours !== 1 ? "s" : ""}`;
  }
  const days = Math.floor(seconds / 86400);
  return `${days} day${days !== 1 ? "s" : ""}`;
};

export const AzureQueueConnectorConfigEditor: React.FC<
  AzureQueueConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AzureQueueConnectorConfig>(() =>
    parseAzureQueueConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAzureQueueConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AzureQueueConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle connection string change
  const handleConnectionStringChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, connectionString: e.target.value });
  };

  // Handle queue name change
  const handleQueueNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Azure queue names must be lowercase
    const queueName = e.target.value.toLowerCase();
    updateConfig({ ...config, queueName });
  };

  // Handle TTL change
  const handleTtlChange = (value: string | number) => {
    const numValue = typeof value === "string" ? parseInt(value, 10) : value;
    if (!isNaN(numValue)) {
      updateConfig({ ...config, messageTtlSeconds: numValue });
    }
  };

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Find your connection string in{" "}
          <Anchor
            href="https://portal.azure.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Azure Portal
          </Anchor>
          {" → Storage Account → Access keys → Connection string"}
        </Text>
      </Alert>

      {/* Connection String Field */}
      <TextInput
        label="Connection String"
        placeholder="DefaultEndpointsProtocol=https;AccountName=..."
        description="Azure Storage account connection string"
        value={config.connectionString}
        onChange={handleConnectionStringChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Queue Name Field */}
      <TextInput
        label="Queue Name"
        placeholder="form-submissions"
        description="Name of the queue to send messages to (must already exist)"
        value={config.queueName}
        onChange={handleQueueNameChange}
        disabled={disabled}
        required
        size="md"
        error={
          config.queueName &&
          !/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(config.queueName) &&
          config.queueName.length >= 3
            ? "Queue name must be 3-63 characters, lowercase letters, numbers, and hyphens only"
            : undefined
        }
      />

      {/* Message TTL Field */}
      <NumberInput
        label="Message Time-to-Live (seconds)"
        placeholder="604800"
        description={`How long messages stay in the queue. Current: ${formatTtlDescription(
          config.messageTtlSeconds
        )}. Use -1 for never expire.`}
        value={config.messageTtlSeconds}
        onChange={handleTtlChange}
        disabled={disabled}
        size="md"
        min={-1}
        max={604800} // Azure max is 7 days
        allowNegative
        allowDecimal={false}
      />
    </Stack>
  );
};
